import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import PageHeader from "../../components/DashboardComponents/PageHeader"
import ListingTabs from "../../components/Listing/ListingTabs"
import SearchInput from "../../components/Listing/SearchInput"
import FilterDropdown from "../../components/Listing/FilterDropdown"
import Pagination from "../../components/Listing/Pagination"
import CustomButton from "../../components/OnboardingComponents/CustomButton"
import EmptyState from "../../components/Common/EmptyState"
import TruncatedText from "../../components/Common/TruncatedText"
import { Avatar } from "../../components/DashboardComponents/AvatarGroup"
import { ICONS } from "../../constants/icons"
import { get } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { getApiErrorMessage } from "../../helpers/apiError"
import {
  FLAG_LIST_INCLUDES,
  PAGE_SIZE,
  STATUS_FILTER_OPTIONS,
  TAB_KEYS,
  TAB_LABELS,
  TAB_TO_API_STATUS,
  mapApiFlag,
} from "./flagData"

const SKELETON_ROWS = 8
const TABLE_COLUMNS = 6
const SEARCH_DEBOUNCE_MS = 300

const buildFlagListParams = ({
  page,
  search,
  statusTab,
  statusFilter,
}) => {
  const params = {
    page,
    page_size: PAGE_SIZE,
    "with[]": FLAG_LIST_INCLUDES,
  }

  if (search) params.search = search

  if (statusFilter) {
    params.status = statusFilter
  } else {
    const status = TAB_TO_API_STATUS[statusTab]
    if (status) params.status = status
  }

  return params
}

const FlagsListing = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState("all")
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [flags, setFlags] = useState([])
  const [totalItems, setTotalItems] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(search.trim()),
      SEARCH_DEBOUNCE_MS,
    )
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearch, activeTab, statusFilter])

  useEffect(() => {
    let cancelled = false

    const fetchFlags = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const response = await get(endpoints.getFlags, {
          params: buildFlagListParams({
            page: currentPage,
            search: debouncedSearch,
            statusTab: activeTab,
            statusFilter,
          }),
        })

        if (cancelled) return

        if (response?.data?.success) {
          const result = response.data.result || {}
          const list = Array.isArray(result) ? result : result.data || []
          const rows = list.map(mapApiFlag)
          const newTotal = result.total ?? rows.length
          setFlags(rows)
          setTotalItems(newTotal)

          const totalPages = Math.max(1, Math.ceil(newTotal / PAGE_SIZE))
          const safePage = Math.min(currentPage, totalPages)
          if (safePage !== currentPage) {
            setCurrentPage(safePage)
          }
        } else {
          setError(getApiErrorMessage(response, "Failed to load flags"))
          setFlags([])
          setTotalItems(0)
        }
      } catch (err) {
        if (cancelled) return
        setError(getApiErrorMessage(err, "Failed to load flags"))
        setFlags([])
        setTotalItems(0)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    fetchFlags()
    return () => {
      cancelled = true
    }
  }, [activeTab, currentPage, debouncedSearch, statusFilter])

  const tabItems = TAB_KEYS.map(key => ({
    key,
    label: TAB_LABELS[key],
    ...(key === activeTab ? { count: totalItems } : {}),
  }))

  return (
    <div className="page-content">
      <div className="flags-listing">
        <PageHeader
          className="flags-listing__header"
          title="My Flags"
          subtitle="Reports submitted by you."
          actions={
            <div className="flags-listing__actions">
              <SearchInput
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder="Search by name..."
                iconSrc={ICONS.search}
                className="flags-listing__search"
              />
              <FilterDropdown
                filters={[
                  {
                    key: "status",
                    type: "select",
                    label: "Status",
                    multiple: false,
                    options: STATUS_FILTER_OPTIONS,
                    value: statusFilter,
                    onChange: next => {
                      setStatusFilter(
                        Array.isArray(next) ? next[0] || "" : next || "",
                      )
                      setActiveTab("all")
                    },
                  },
                ]}
              />
            </div>
          }
        />

        <div className="flags-listing__tabs">
          <ListingTabs
            items={tabItems}
            activeKey={activeTab}
            onChange={key => {
              setActiveTab(key)
              setStatusFilter("")
            }}
          />
        </div>

        <div className="flags-listing__table-wrapper">
          <div
            className="flags-listing__table"
            role="table"
            aria-label="My flags"
            aria-busy={isLoading || undefined}
          >
            <div
              className="flags-listing__row flags-listing__row--head"
              role="row"
            >
              <div role="columnheader">User Profile</div>
              <div role="columnheader">Module</div>
              <div role="columnheader">Reason</div>
              <div role="columnheader">Status</div>
              <div role="columnheader">Submitted</div>
              <div role="columnheader">Action</div>
            </div>

            {isLoading &&
              Array.from({ length: SKELETON_ROWS }).map((_, index) => (
                <div
                  key={`skeleton-${index}`}
                  className="flags-listing__row flags-listing__row--skeleton"
                  role="row"
                  aria-hidden="true"
                >
                  {Array.from({ length: TABLE_COLUMNS }).map((__, cellIdx) => (
                    <div key={cellIdx} className="flags-listing__skeleton-cell">
                      <div className="flags-listing__skeleton-bar" />
                    </div>
                  ))}
                </div>
              ))}

            {!isLoading &&
              flags.map(flag => (
                <div key={flag.id} className="flags-listing__row" role="row">
                  <div className="flags-listing__user" role="cell">
                    <Avatar src={flag.avatar} name={flag.name} size="sm" />
                    <TruncatedText className="flags-listing__name">
                      {flag.name}
                    </TruncatedText>
                  </div>
                  <div className="flags-listing__module" role="cell">
                    <span
                      className={`flags-listing__module-badge flags-listing__module-badge--${flag.moduleTone}`}
                    >
                      {flag.moduleLabel}
                    </span>
                  </div>
                  <div className="flags-listing__reason" role="cell">
                    <TruncatedText>{flag.reason}</TruncatedText>
                  </div>
                  <div className="flags-listing__status-cell" role="cell">
                    <span
                      className={`flags-listing__status flags-listing__status--${flag.statusTone}`}
                    >
                      {flag.statusLabel}
                    </span>
                  </div>
                  <div className="flags-listing__date" role="cell">
                    <TruncatedText>{flag.submittedLabel}</TruncatedText>
                  </div>
                  <div className="flags-listing__action" role="cell">
                    <CustomButton
                      variant="secondary"
                      size="sm"
                      type="button"
                      className="flags-listing__detail-btn"
                      trailingIconSrc={ICONS.arrowRight12}
                      trailingIconAlt=""
                      onClick={() => navigate(`/flags/${flag.id}`)}
                    >
                      View Detail
                    </CustomButton>
                  </div>
                </div>
              ))}

            {!isLoading && error && (
              <EmptyState
                title="Unable to load flags"
                description={error}
              />
            )}

            {!isLoading && !error && flags.length === 0 && (
              <EmptyState
                title="No Flags Yet"
                description="Reports you submit will show up here."
              />
            )}
          </div>
        </div>

        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          pageSize={PAGE_SIZE}
          onPageChange={setCurrentPage}
          itemLabel="Flags"
          prevIconSrc={ICONS.chevronLeft}
          nextIconSrc={ICONS.chevronRight}
        />
      </div>
    </div>
  )
}

export default FlagsListing
